import { FC, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ResourceType } from '@dbt-labs/dbt-dag';
import {
  IconButton,
  IconButtonProps,
  RyeconClose,
  RyeconCode,
  RyeconCompass,
  RyeconLinkExternal,
  RyeconShare,
  Sizes,
  Tooltip,
} from '@dbt-labs/sourdough';

import { AutoExposureChip } from '@/components/AutoExposureChip';
import { ResourceChip } from '@/components/ResourceChip';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useAccountProjects } from '@/hooks/discovery/accountProjects';
import { useGenericResource } from '@/hooks/discovery/types';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { sanitizeCoreFilePath } from '@/util/file';

type ExposureDetailsIconButtonProps = Omit<IconButtonProps, 'ryecon'> & {
  exposureUrl: string | null;
};

export const ExposureDetailsIconButton = ({
  exposureUrl,
  ...props
}: ExposureDetailsIconButtonProps) => {
  const label = exposureUrl ? 'View this exposure' : undefined;
  const icon = (
    <IconButton
      ryecon={RyeconLinkExternal}
      isDisabled={!exposureUrl}
      onClick={() => {
        if (!exposureUrl) return;
        window.open(exposureUrl, '_blank');
      }}
      label={label}
      tooltip={label}
      size={Sizes.md}
      {...props}
    />
  );

  if (exposureUrl) return <>{icon}</>;
  return <Tooltip content={'Exposure has no URL provided'}>{icon}</Tooltip>;
};

type PanelHeaderProps = {
  projectId?: null;
  environmentId: number;
  uniqueId: string;
  resourceType: ResourceType;
  files: (string | null | undefined)[];
  // TODO: Add isExternalModel prop to ResourcePanelHeader
};

export const ResourcePanelHeader: FC<PanelHeaderProps> = (props) => {
  const setSearchParams = useSearchParams()[1];
  const linkToResource = useLinkToExplorer('resourceDetails');
  const navigate = useNavigate();
  const { accountId, hasDeveloperLicense } = useDbtCloudAccount();
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const [accountProjectsQuery] = useAccountProjects({ accountId });
  const isProject = false;
  const tooltip = isProject ? 'View Project' : 'View Resource';
  const getCloudIdeLink = useLinkToDbtCloud('cloudIde');

  const uniqueId = useMemo(() => parseUniqueId(props.uniqueId), [props.uniqueId]);
  const resourceQuery = useGenericResource(uniqueId);
  const resource = resourceQuery.data;
  const resourceProjectId = resource?.projectId;

  const coreProjectName = accountProjectsQuery.data?.account?.projects?.find(
    (x) => x.projectId === resourceProjectId,
  )?.coreProjectName;

  if (!accountId) return null;

  const autoExposureBiProvider = resource?.autoBiProvider;
  const exposureUrl = uniqueId.resourceType === 'exposure' ? resource?.url : null;
  return (
    <div className="flex w-full items-center border-b border-borderMuted p-4">
      <span className="flex-1">
        {!autoExposureBiProvider && <ResourceChip resourceType={props.resourceType} />}
        {!!autoExposureBiProvider && (
          <AutoExposureChip biProvider={autoExposureBiProvider} />
        )}
      </span>
      <span className="flex-0 flex items-center gap-2">
        {autoExposureBiProvider && exposureUrl && (
          <ExposureDetailsIconButton
            exposureUrl={exposureUrl}
            size={Sizes.lg}
            className="size-6 align-middle"
          />
        )}
        <IconButton
          size={Sizes.lg}
          ryecon={RyeconCompass}
          className="size-6 align-middle"
          label={tooltip}
          tooltip={tooltip}
          tooltipPlacement="bottom-end"
          isDisabled={isProject ? !props.projectId : !resourceProjectId}
          onClick={() => {
            const projectId = resourceProjectId;
            if (!projectId) {
              return;
            }
            const uniqueId = props.uniqueId;
            const resourceUrl = linkToResource({
              accountId,
              projectId,
              environmentType: getTargetEnvironmentType(props.environmentId),
              dbtUniqueId: uniqueId,
            });
            navigate(resourceUrl);
          }}
        />
        {getCloudIdeLink && (
          <IconButton
            size={Sizes.lg}
            ryecon={RyeconCode}
            label="Open in IDE"
            tooltip="Open in IDE"
            tooltipPlacement="bottom-end"
            className="size-6 align-middle"
            isDisabled={
              isProject ||
              !hasDeveloperLicense ||
              !props.files.length ||
              !!(uniqueId?.packageName && uniqueId.packageName !== coreProjectName)
            }
            onClick={() => {
              if (isProject || !props.files) {
                return false;
              }

              window.open(
                getCloudIdeLink({
                  accountId,
                  projectId: resourceProjectId ?? 0,
                  file: props.files
                    .map((file) => sanitizeCoreFilePath(file, coreProjectName))
                    .filter(truthy),
                }),
                'cloud-ide', // re-use the same window/tab if opening multiple files
              );
            }}
          />
        )}
        <IconButton
          size={Sizes.lg}
          ryecon={RyeconShare}
          label="Copy Link to Page"
          tooltip="Copy Link to Page"
          tooltipPlacement="bottom-end"
          className="size-6 align-middle"
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Copied link to clipboard', { id: 'copy-link' });
          }}
        />
        <IconButton
          size={Sizes.lg}
          ryecon={RyeconClose}
          className="size-6 align-middle"
          label="Close Panel"
          tooltip="Close Panel"
          tooltipPlacement="bottom-end"
          testId="close-resource-panel-button"
          onClick={() => {
            setSearchParams((params) => {
              params.delete('uniqueId');
              params.delete('projectId');
              params.delete('environmentId');
              return params;
            });
          }}
        />
      </span>
    </div>
  );
};
